import { Navigate, useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';

// layouts
import DashboardLayout from './layouts/dashboard';
import MainLayout from './layouts/MainLayout';
import LogoOnlyLayout from './layouts/LogoOnlyLayout';
//

//
import User from './pages/User';
import CreateStaff from './pages/Staff/CreateStaff';
import EditStaff from './pages/Staff/EditStaff';
import Login from './pages/Login';
import Register from './pages/Register';
import PasswordReset from './pages/PasswordReset';
import WhitelabelLogin from './pages/WhitelabelLogin';

import NotFound from './pages/Page404';
import Products from './pages/Products';
import DashboardApp from './pages/DashboardApp';
import Add from './pages/Users/NewUser';
import UpdateService from './pages/UpdateServices';
import UpdateDiscount from './pages/UpdateDiscount';
import Transaction from './pages/Transaction';
import AppSetting from './pages/AppSetting';
import CreateDataProduct from './pages/Service/NewProduct';
import AppSystemSetting from './pages/AppSystemSetting';
import PaymentSetting from './pages/PaymentSetting';
import CustomerSetting from './pages/CustomersSettings';
import ResellingProduct from './pages/ResellingProducts';
import UpdateResellingService from './pages/UpdateResellingServices';
import UserManagement from './pages/UserManagement';
import UserDetails from './pages/UserDetails';
import Analytics from './pages/Analytics';
import DashboardMain from './pages/DashboardMain';
import CreateApp from './pages/CreateApp';
import BillingPage from './pages/BillingPage';
import GlobalBillingPage from './pages/GlobalBillingPage';
import ServiceActivationPage from './pages/ServiceActivationPage';
// eslint-disable-next-line import/no-unresolved, import/extensions
import AdCampaigns from './pages/AdCampaigns';
// eslint-disable-next-line import/no-unresolved, import/extensions
import CampaignDetails from './pages/CampaignDetails';
import CompareCampaigns from './pages/CompareCampaigns';
import AuditLogsPage from './pages/AuditLogsPage';
import PermissionGuard from './components/PermissionGuard';
import { PERMISSIONS } from './utils/hasPermission';

// ----------------------------------------------------------------------

export default function Router() {
  const { isAuthenticated } = useSelector((state) => state.auth);
  const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
  const isBootstrapped = !!bootstrappedApp;

  const defaultRedirect = isBootstrapped ? '/dashboard/app' : '/dashboard/main';

  let loginElement;
  if (isAuthenticated) {
    loginElement = <Navigate to={defaultRedirect} />;
  } else if (isBootstrapped) {
    loginElement = <WhitelabelLogin />;
  } else {
    loginElement = <Login />;
  }

  let registerElement;
  if (isBootstrapped) {
    registerElement = <Navigate to="/login" replace />;
  } else if (isAuthenticated) {
    registerElement = <Navigate to={defaultRedirect} />;
  } else {
    registerElement = <Register />;
  }

  const mainElement = isBootstrapped ? <Navigate to="/dashboard/app" replace /> : <DashboardMain />;
  const createAppElement = isBootstrapped ? <Navigate to="/dashboard/app" replace /> : <CreateApp />;

  return useRoutes([
    {
      path: '/dashboard',
      element: <MainLayout />,
      children: [
        { path: 'main', element: mainElement },
        { path: 'create-app', element: createAppElement },
        { path: 'global-billing', element: <GlobalBillingPage /> },
        { path: 'audit-logs', element: <AuditLogsPage /> },
      ],
    },
    {
      path: '/dashboard',
      element: <DashboardLayout />,
      children: [
        { path: 'app', element: <DashboardApp /> },
        {
          path: 'user-management',
          element: (
            <PermissionGuard permission={PERMISSIONS.AGENTS_READ} redirectTo="/dashboard/app">
              <UserManagement />
            </PermissionGuard>
          ),
        },
        {
          path: 'user-details',
          element: (
            <PermissionGuard permission={PERMISSIONS.AGENTS_READ} redirectTo="/dashboard/app">
              <UserDetails />
            </PermissionGuard>
          ),
        },
        {
          path: 'user-details/:id',
          element: (
            <PermissionGuard permission={PERMISSIONS.AGENTS_READ} redirectTo="/dashboard/app">
              <UserDetails />
            </PermissionGuard>
          ),
        },
        {
          path: 'staff',
          element: (
            <PermissionGuard permission={PERMISSIONS.STAFF_READ} redirectTo="/dashboard/app">
              <User />
            </PermissionGuard>
          ),
        },
        {
          path: 'staff/create',
          element: (
            <PermissionGuard permission={PERMISSIONS.STAFF_MANAGE} redirectTo="/dashboard/app">
              <CreateStaff />
            </PermissionGuard>
          ),
        },
        {
          path: 'staff/edit/:id',
          element: (
            <PermissionGuard permission={PERMISSIONS.STAFF_MANAGE} redirectTo="/dashboard/app">
              <EditStaff />
            </PermissionGuard>
          ),
        },
        { path: 'user/notifications', element: <Add /> },
        {
          path: 'service/add-data-product',
          element: (
            <PermissionGuard permission={PERMISSIONS.SERVICES_WRITE} redirectTo="/dashboard/app">
              <CreateDataProduct />
            </PermissionGuard>
          ),
        },
        {
          path: 'services',
          element: (
            <PermissionGuard permission={PERMISSIONS.SERVICES_READ} redirectTo="/dashboard/app">
              <Products />
            </PermissionGuard>
          ),
        },
        {
          path: 'services/:category',
          element: (
            <PermissionGuard permission={PERMISSIONS.SERVICES_WRITE} redirectTo="/dashboard/app">
              <UpdateService />
            </PermissionGuard>
          ),
        },
        {
          path: 'reselling',
          element: (
            <PermissionGuard permission={PERMISSIONS.SERVICES_READ} redirectTo="/dashboard/app">
              <ResellingProduct />
            </PermissionGuard>
          ),
        },
        {
          path: 'reselling/:category',
          element: (
            <PermissionGuard
              permission={[PERMISSIONS.SERVICES_WRITE, PERMISSIONS.RESELLING_MANAGE]}
              redirectTo="/dashboard/app"
            >
              <UpdateResellingService />
            </PermissionGuard>
          ),
        },
        { path: 'discount', element: <UpdateDiscount /> },
        {
          path: 'transaction',
          element: (
            <PermissionGuard permission={PERMISSIONS.TRANSACTIONS_READ} redirectTo="/dashboard/app">
              <Transaction />
            </PermissionGuard>
          ),
        },
        {
          path: 'analytics',
          element: (
            <PermissionGuard permission={PERMISSIONS.TRANSACTIONS_ANALYTICS} redirectTo="/dashboard/app">
              <Analytics />
            </PermissionGuard>
          ),
        },

        {
          path: 'campaigns',
          element: isAuthenticated ? (
            <PermissionGuard permission={PERMISSIONS.CAMPAIGNS_READ} redirectTo="/dashboard/app">
              <AdCampaigns />
            </PermissionGuard>
          ) : (
            <Navigate to="/login" />
          ),
        },
        {
          path: 'campaigns/compare',
          element: isAuthenticated ? (
            <PermissionGuard permission={PERMISSIONS.CAMPAIGNS_READ} redirectTo="/dashboard/app">
              <CompareCampaigns />
            </PermissionGuard>
          ) : (
            <Navigate to="/login" />
          ),
        },
        {
          path: 'campaigns/:id',
          element: isAuthenticated ? (
            <PermissionGuard permission={PERMISSIONS.CAMPAIGNS_READ} redirectTo="/dashboard/app">
              <CampaignDetails />
            </PermissionGuard>
          ) : (
            <Navigate to="/login" />
          ),
        },

        {
          path: 'settings',
          element: (
            <PermissionGuard
              permission={[PERMISSIONS.SYSTEM_SETTINGS_READ, PERMISSIONS.SYSTEM_SETTINGS_UPDATE]}
              redirectTo="/dashboard/app"
            >
              <AppSetting />
            </PermissionGuard>
          ),
        },
        {
          path: 'settings/app',
          element: (
            <PermissionGuard
              permission={[PERMISSIONS.SYSTEM_SETTINGS_READ, PERMISSIONS.SYSTEM_SETTINGS_UPDATE]}
              redirectTo="/dashboard/app"
            >
              <AppSystemSetting />
            </PermissionGuard>
          ),
        },
        {
          path: 'settings/payment',
          element: (
            <PermissionGuard
              permission={[PERMISSIONS.SYSTEM_SETTINGS_READ, PERMISSIONS.SYSTEM_SETTINGS_UPDATE]}
              redirectTo="/dashboard/app"
            >
              <PaymentSetting />
            </PermissionGuard>
          ),
        },
        {
          path: 'settings/customers',
          element: (
            <PermissionGuard
              permission={[PERMISSIONS.SYSTEM_SETTINGS_READ, PERMISSIONS.SYSTEM_SETTINGS_UPDATE]}
              redirectTo="/dashboard/app"
            >
              <CustomerSetting />
            </PermissionGuard>
          ),
        },
        {
          path: 'billing',
          element: <BillingPage />,
        },
        {
          path: 'activations',
          element: <ServiceActivationPage />,
        },
        {
          path: 'audit-logs',
          element: (
            <PermissionGuard
              permission={PERMISSIONS.AUDIT_READ}
              redirectTo="/dashboard/app"
            >
              <AuditLogsPage />
            </PermissionGuard>
          ),
        },
      ],
    },
    {
      path: 'login',
      element: loginElement,
    },
    {
      path: 'register',
      element: registerElement,
    },

    {
      path: 'password-reset',
      element: isAuthenticated ? <Navigate to={defaultRedirect} /> : <PasswordReset />,
    },
    {
      path: '/',
      element: <LogoOnlyLayout />,
      children: [
        { path: '/', element: <Navigate to={defaultRedirect} /> },
        { path: '404', element: <NotFound /> },
        { path: '*', element: <Navigate to="/404" /> },
      ],
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}
